import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon } from '@heroicons/react/24/outline';
import Notification from '../../components/notification/notification';
import { PageHeader } from '../../components/ui';
import discountApi from '../../services/discountApi';
import publicCouponApi from '../../services/publicCouponApi';
import FinButton from '../connect/finance/fin-button';
import FinancePageShell from '../finance/pages/finance-page-shell';
import CampaignList from './components/CampaignList';
import LegacyCouponList from './components/LegacyCouponList';
import NewCampaignModal from './components/NewCampaignModal';
import EditCampaignModal from './components/EditCampaignModal';
import LegacyCouponUpsertModal from './components/LegacyCouponUpsertModal';
import { ACTIVE_FILTER_STATUSES, ARCHIVED_FILTER_STATUSES } from './lib/campaign-status';

export const PAGE_COPY = {
  title: 'Coupons',
  description: 'Public campaigns and legacy discount coupons.',
  newCampaign: 'New campaign',
  ownerNotFound: 'Couldn’t open that coupon.',
};

// Spec 2026-10-21 §2: one Coupons page on the Finance ground — the Public
// campaigns card (Active / Archived), the Legacy discounts card, the
// three-step New campaign modal and the one-screen Edit modal. The legacy
// modal is today's, unchanged.
const Coupons = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const companyId = globalCompany?.id;

  const [publicReload, setPublicReload] = useState(0);
  const [legacyReload, setLegacyReload] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [legacyModal, setLegacyModal] = useState({ open: false, coupon: null });
  const [notf, setNotf] = useState({ display: false, type: 'error', message: '' });

  const showError = useCallback((error) => setNotf({ display: true, type: 'error', message: error }), []);
  const closeNotification = useCallback(() => setNotf((prev) => ({ ...prev, display: false })), []);
  const reloadPublic = useCallback(() => setPublicReload((n) => n + 1), []);

  // "This code is already used by …": the name opens that coupon (spec
  // §2.4). Restaurant roles hold the List permissions, not `discount-get`,
  // so both kinds are read through List by the typed code (case-insensitive
  // after the api round) and matched by id.
  const openOwner = useCallback(
    async (owner, code) => {
      try {
        if (owner.kind === 'legacy') {
          const res = await discountApi.list(
            `?companyid=${encodeURIComponent(companyId)}&code=${encodeURIComponent(code)}&pageNumber=1&pageSize=10`,
          );
          const found = (res?.data?.data || []).find((coupon) => coupon.id === owner.id);
          if (found) setLegacyModal({ open: true, coupon: found });
          else showError(PAGE_COPY.ownerNotFound);
          return;
        }
        const res = await publicCouponApi.list({
          companyid: companyId,
          code,
          statuses: [...ACTIVE_FILTER_STATUSES, ...ARCHIVED_FILTER_STATUSES],
          pageNumber: 1,
          pageSize: 10,
        });
        const found = (res?.data?.data || []).find((coupon) => coupon.id === owner.id);
        if (found) setEditing(found);
        else showError(PAGE_COPY.ownerNotFound);
      } catch {
        showError(PAGE_COPY.ownerNotFound);
      }
    },
    [companyId, showError],
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-fin-canvas">
      <FinancePageShell>
        <PageHeader
          title={PAGE_COPY.title}
          description={PAGE_COPY.description}
          actions={
            // FinButton ignores className by design, so the phone width and
            // height come from the wrapper: full width and h-11 below sm.
            <div className="w-full sm:w-auto [&>button]:h-11 [&>button]:w-full sm:[&>button]:h-auto sm:[&>button]:w-auto">
              <FinButton variant="primary" onClick={() => setCreating(true)}>
                <PlusIcon className="mr-1.5 h-4 w-4 stroke-[2.5]" aria-hidden="true" />
                {PAGE_COPY.newCampaign}
              </FinButton>
            </div>
          }
        />
        <CampaignList companyId={companyId} reloadKey={publicReload} onEdit={setEditing} />
        <LegacyCouponList
          companyId={companyId}
          reloadKey={legacyReload}
          onAdd={() => setLegacyModal({ open: true, coupon: null })}
          onEdit={(coupon) => setLegacyModal({ open: true, coupon })}
        />
      </FinancePageShell>

      <NewCampaignModal
        open={creating}
        companyId={companyId}
        onClose={() => setCreating(false)}
        onCreated={reloadPublic}
        onOpenOwner={openOwner}
      />

      <EditCampaignModal
        open={Boolean(editing)}
        coupon={editing}
        companyId={companyId}
        onClose={() => setEditing(null)}
        onSaved={reloadPublic}
        onOpenOwner={openOwner}
      />

      <LegacyCouponUpsertModal
        show={legacyModal.open}
        onClose={() => setLegacyModal((prev) => ({ ...prev, open: false }))}
        initialData={legacyModal.coupon}
        onSuccess={() => setLegacyReload((n) => n + 1)}
        onError={showError}
      />

      <Notification display={notf.display} type={notf.type} message={notf.message} onClose={closeNotification} />
    </div>
  );
};

export default Coupons;
